import React from 'react';

import styles from './MultiSelector.module.css';

import { useState } from 'react';
import useClickOutside from '../../../hooks/useClickOutside';
import { useTranslation } from 'react-i18next';
import useTooltip from '../../../hooks/useTooltip';

import { OptionsMultiSelector } from '../../../interfaces/Input';
import Dropdown from '../Dropdown/Dropdown';

interface Props {
    id?: string,
    label?: string
    options: OptionsMultiSelector[]
    error?: boolean,
    errorMsg?: string
    handleChange: (id: number | string) => void
    disabledAllOption?: boolean,
    style?: React.CSSProperties
}

const MultiSelector: React.FC<Props> = ({
    id,
    label,
    options,
    error,
    errorMsg,
    handleChange,
    disabledAllOption,
    style,
}) => {

    const { t } = useTranslation();

    const [showOptions, setShowOptions] = useState(false);

    const handleClick = () => setShowOptions(oldOptions => !oldOptions);

    const handleClickOption = (id: number | string) => {
        handleChange(id);
    };

    const getStylePos = (pos: DOMRect) => ({ top: `${pos.bottom + 5}px`, left: `${pos.left}px`, width: pos.width });

    const { tooltipRef, getStyles } = useTooltip(getStylePos);

    const setFalse = () => setShowOptions(false);

    const wrapperRef = useClickOutside(setFalse, tooltipRef);

    const optionsWithAll = disabledAllOption ? options : [{ id: -1, name: t('all'), checked: options.every(option => option.checked) }, ...options];

    const getDefaultLabel = () => {
        const optionsChecked = options.filter(option => option.checked);
        if (optionsChecked.length === 0) return '';
        if (optionsChecked.length === options.length && !disabledAllOption) return t('all');
        return optionsChecked[0].name + (optionsChecked.length > 1 ? ` (+${optionsChecked.length - 1})` : '');
    };

    return (
        <div className={styles.inputWrapper} id={id} ref={tooltipRef} style={style}>
            <div ref={wrapperRef} className={`${styles.selectContainer} ${showOptions ? styles.selectShowOptions : ''}`}>
                <input
                    onClick={handleClick}
                    className={`${styles.inputText} ${error ? 'errorOutline' : ''}`}
                    type='button'
                    defaultValue={label ?? getDefaultLabel()}
                />
                <Dropdown
                    show={showOptions}
                    handleClickOutside={() => setShowOptions(false)}
                    elementRef={tooltipRef}
                >
                    <div
                        className={styles.optionsContainer}
                        style={{
                            ...getStyles(),
                        }}
                        ref={wrapperRef}
                    >
                        {optionsWithAll.map(option =>
                            <button onClick={() => handleClickOption(option.id)} key={option.id} className={styles.buttonOption}>
                                <input type='checkbox' checked={option.checked} readOnly />
                                <span>{option.name}</span>
                            </button>
                        )}
                    </div>
                </Dropdown>
            </div>
            {errorMsg && <p className={'errorMsg'}>{errorMsg}</p>}
        </div>
    );
};

export default MultiSelector;